import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, MapPin, Clock, Video, Info } from "lucide-react";
import { api } from "../lib/data";
import { useSEO } from "../lib/useSEO";
import { PageDecor } from "../components/decor/PageDecor";
import RetourApp from "../components/RetourApp";

/* La page de présentation des workshops heels, dans Événements. Les séances, les prix et
   le numéro TWINT viennent de la console (`GET /api/workshops`) : rien n'est écrit en dur
   ici, l'école change ses dates sans redéploiement. */
export default function WorkshopsHeels() {
  const [cat, setCat] = React.useState(null);
  useSEO({
    title: "Workshops heels technique & freestyle — 1000 ARTS Cugy VD",
    description:
      "Workshops heels du samedi après-midi à Cugy VD : technique et freestyle, 1h30 par séance, tous niveaux. CHF 30.– la séance ou le semestre complet au forfait.",
    canonical: "/evenements/workshops-heels",
  });

  React.useEffect(() => {
    api.get("/workshops").then((r) => setCat(r.data)).catch(() => setCat(null));
  }, []);

  const parSemestre = (n) => (cat?.seances || []).filter((s) => Number(s.semestre) === n);

  return (
    <div className="pt-32 pb-24 relative overflow-hidden mesh-bg" data-testid="page-workshops-heels">
      <PageDecor />
      <section className="max-w-[1100px] mx-auto px-6 md:px-12 relative z-10">
        {/* Ouvert depuis l'app (app=1) : « ← Retour à l'app » (Jen, 01.11.2026). */}
        <RetourApp />
        {/* Un seul bloc d'en-tête : titre, promesse, les trois repères et le bouton à
            gauche ; la photo tient toute la hauteur à droite. Avant, la photo était plus
            haute que le paragraphe et laissait une colonne blanche de 200 px. */}
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-14 items-stretch">
          <div className="flex flex-col">
            <div className="eyebrow">Événements · Heels</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-2 leading-[0.95]">
              Workshops heels<br />technique &amp; freestyle
            </h1>
            <p className="text-base text-[var(--muted)] leading-relaxed mt-5">
              Un samedi après-midi par mois, on prend le temps de travailler le heels pour de
              vrai : la technique d'abord (posture, équilibre, marche, lignes), puis le
              freestyle, là où le style devient le tien. Chaque séance dure 1h30 et s'adresse à
              <strong> tous les niveaux</strong> — débutante curieuse comme danseuse confirmée.
            </p>

            <ul className="mt-8 space-y-4" data-testid="workshops-reperes">
              {[
                { Icon: Clock, titre: "1h30 par séance", detail: `CHF ${cat?.seances?.[0]?.prix ?? 30}.– le workshop` },
                { Icon: Video, titre: "Vidéo en fin de semestre", detail: "Le dernier workshop dure 2h et tu repars avec ta vidéo" },
                { Icon: MapPin, titre: "Chez 1000 Arts", detail: cat?.lieu || "Salle B — Cugy VD" },
              ].map(({ Icon, titre, detail }) => (
                <li key={titre} className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: "var(--primary-tint)", color: "var(--primary)" }}>
                    <Icon size={16} />
                  </span>
                  <span className="text-sm">
                    <strong className="text-[var(--text)]">{titre}</strong>
                    <span className="block text-[var(--muted)]">{detail}</span>
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-8 lg:mt-auto lg:pt-8">
              <Link to="/inscription/workshop" className="btn-primary" data-testid="workshops-cta-haut">
                M'inscrire aux workshops <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          <div className="relative order-first lg:order-none">
            <img
              src="/img/f71acbdfff.webp"
              alt="Cours de heels chez 1000 Arts — travail en talons"
              loading="lazy"
              className="w-full h-full rounded-3xl object-cover aspect-[16/11] lg:aspect-[4/5]"
              style={{ maxHeight: 560 }}
              data-testid="workshops-photo"
            />
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 mt-14">
        {[1, 2].map((n) => parSemestre(n).length > 0 && (
          <div key={n} className="flex flex-col" data-testid={`workshops-semestre-${n}`}>
            <h2 className="font-display text-base md:text-lg tracking-tight">
              Les prochains workshops {n === 1 ? "— premier semestre" : "— second semestre"}
            </h2>
            <ul className="mt-4 space-y-2">
              {parSemestre(n).map((s) => (
                <li key={s.id} className="flex items-center justify-between p-4 rounded-2xl"
                  style={{ background: "var(--primary-tint)" }}>
                  <span className="text-sm">
                    <strong>{s.libelle.split(" · ")[0]}</strong>
                    <span className="block text-[var(--muted)]">
                      {s.libelle.split(" · ").slice(1).join(" · ")}
                    </span>
                  </span>
                  <span className="font-display text-lg whitespace-nowrap">CHF {s.prix}.–</span>
                </li>
              ))}
            </ul>
            {(cat?.forfaits || []).includes(n) ? (
              <div className="mt-3 lg:mt-auto flex items-start gap-3 p-4 rounded-2xl"
                style={{ background: "#FFF8E7", border: "1px solid #F5D57A" }}>
                <Check size={16} className="mt-0.5 shrink-0" style={{ color: "#B7791F" }} />
                <div className="text-sm" style={{ color: "#8B6914" }}>
                  Les {parSemestre(n).length} workshops du semestre d'un coup :
                  <strong> CHF {cat.forfait_prix}.–</strong> au lieu de CHF{" "}
                  {parSemestre(n).reduce((t, s) => t + s.prix, 0)}.–
                </div>
              </div>
            ) : (
              /* Le forfait suppose les 5 séances devant soi : dès que le semestre a
                 commencé, il disparaît. Autant le dire, sinon la colonne se termine sur
                 un blanc et la famille cherche le prix groupé. */
              <div className="mt-3 lg:mt-auto flex items-start gap-3 p-4 rounded-2xl"
                style={{ background: "#F9FAFB", border: "1px solid var(--border-soft)" }}>
                <Info size={16} className="mt-0.5 shrink-0" style={{ color: "var(--muted)" }} />
                <div className="text-sm text-[var(--muted)]">
                  Le semestre a commencé : le forfait groupé n'est plus proposé. Les séances
                  restantes se prennent <strong>à l'unité</strong>, autant que tu veux.
                </div>
              </div>
            )}
          </div>
        ))}
        </div>

        <div className="mt-12 flex items-start gap-3 p-5 rounded-2xl"
          style={{ background: "#F9FAFB", border: "1px solid var(--border-soft)" }}>
          <Info size={16} className="mt-0.5 shrink-0" style={{ color: "var(--primary)" }} />
          <div className="text-sm text-[var(--muted)] leading-relaxed">
            Le nombre de participantes est limité. <strong>Ta place n'est validée qu'au
            paiement</strong> par TWINT{cat?.twint ? ` au ${cat.twint}` : ""} : tant que le
            montant n'est pas versé, elle peut être prise. Une fois validée, l'inscription
            n'est ni remboursable ni modifiable — tu peux par contre revendre ta place.
          </div>
        </div>

        <div className="mt-8">
          <Link to="/inscription/workshop" className="btn-primary" data-testid="workshops-cta-inscription">
            M'inscrire aux workshops <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
